// src/components/UI.jsx
import React, { useRef } from "react";

// ── Button ──────────────────────────────────────────────────────────────────
export const Btn = ({ children, onClick, variant = "primary", style = {}, disabled = false, type = "button" }) => {
  const variants = {
    primary:   { background: "#2d7a3a", color: "#fff", border: "none" },
    secondary: { background: "#f5f5f0", color: "#333", border: "1px solid #ddd" },
    danger:    { background: "#e53e3e", color: "#fff", border: "none" },
    ghost:     { background: "transparent", color: "#2d7a3a", border: "1px solid #2d7a3a" },
    warning:   { background: "#d97706", color: "#fff", border: "none" },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...variants[variant],
        padding: "8px 18px", borderRadius: 8, fontFamily: "inherit",
        fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1, transition: "all 0.15s", lineHeight: 1.4, ...style,
      }}
    >
      {children}
    </button>
  );
};

// ── Badge ────────────────────────────────────────────────────────────────────
export const Badge = ({ children, color = "#2d7a3a" }) => (
  <span style={{
    background: color + "22", color, padding: "2px 10px",
    borderRadius: 99, fontSize: 11, fontWeight: 700,
    border: `1px solid ${color}44`, whiteSpace: "nowrap",
  }}>
    {children}
  </span>
);

// ── Input ────────────────────────────────────────────────────────────────────
export const Input = ({ label, value, onChange, type = "text", required, placeholder, style = {}, readOnly }) => (
  <div style={{ marginBottom: 14 }}>
    {label && (
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#e53e3e" }}> *</span>}
      </label>
    )}
    <input
      type={type} value={value} onChange={e => onChange && onChange(e.target.value)}
      placeholder={placeholder} readOnly={readOnly}
      style={{
        width: "100%", padding: "9px 12px", border: "1px solid #ddd", borderRadius: 8,
        fontFamily: "inherit", fontSize: 14, background: readOnly ? "#f0f0f0" : "#fafaf8",
        outline: "none", boxSizing: "border-box", ...style,
      }}
    />
  </div>
);

// ── Textarea ─────────────────────────────────────────────────────────────────
export const Textarea = ({ label, value, onChange, rows = 4, placeholder, required }) => (
  <div style={{ marginBottom: 14 }}>
    {label && (
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#e53e3e" }}> *</span>}
      </label>
    )}
    <textarea
      value={value} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
      style={{
        width: "100%", padding: "9px 12px", border: "1px solid #ddd", borderRadius: 8,
        fontFamily: "inherit", fontSize: 14, resize: "vertical", background: "#fafaf8",
        outline: "none", boxSizing: "border-box",
      }}
    />
  </div>
);

// ── Select ───────────────────────────────────────────────────────────────────
export const Select = ({ label, value, onChange, options = [], required, placeholder = "-- Select --" }) => (
  <div style={{ marginBottom: 14 }}>
    {label && (
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#e53e3e" }}> *</span>}
      </label>
    )}
    <select
      value={value} onChange={e => onChange(e.target.value)}
      style={{
        width: "100%", padding: "9px 12px", border: "1px solid #ddd", borderRadius: 8,
        fontFamily: "inherit", fontSize: 14, background: "#fafaf8", outline: "none",
      }}
    >
      <option value="">{placeholder}</option>
      {options.map(o => {
        const val = typeof o === "string" ? o : o.value;
        const lbl = typeof o === "string" ? o : o.label;
        return <option key={val} value={val}>{lbl}</option>;
      })}
    </select>
  </div>
);

// ── FileUpload ────────────────────────────────────────────────────────────────
export const FileUpload = ({ label, accept, onFile, preview, hint, required }) => {
  const inputRef = useRef();
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>
          {label}{required && <span style={{ color: "#e53e3e" }}> *</span>}
        </label>
      )}
      <div
        onClick={() => inputRef.current.click()}
        style={{
          border: "2px dashed #b2d8b7", borderRadius: 10, padding: "16px 12px", textAlign: "center",
          cursor: "pointer", background: "#f6fbf6", transition: "border-color 0.2s",
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "#2d7a3a"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "#b2d8b7"}
      >
        {preview ? (
          <img src={preview} alt="preview" style={{ maxHeight: 90, maxWidth: "100%", borderRadius: 6, marginBottom: 6 }} />
        ) : (
          <div style={{ fontSize: 30, marginBottom: 4 }}>📎</div>
        )}
        <p style={{ fontSize: 12, color: "#666", margin: 0 }}>{hint || "Click to upload"}</p>
      </div>
      <input
        ref={inputRef} type="file" accept={accept} style={{ display: "none" }}
        onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); e.target.value = ""; }}
      />
    </div>
  );
};

// ── ProgressBar ───────────────────────────────────────────────────────────────
export const ProgressBar = ({ value, label }) => (
  <div style={{ marginBottom: 12 }}>
    {label && <p style={{ fontSize: 12, color: "#2d7a3a", marginBottom: 4 }}>{label} {Math.round(value)}%</p>}
    <div style={{ background: "#e8f5e9", borderRadius: 99, height: 8, overflow: "hidden" }}>
      <div style={{ background: "#2d7a3a", height: "100%", width: `${value}%`, borderRadius: 99, transition: "width 0.3s" }} />
    </div>
  </div>
);

// ── Modal ─────────────────────────────────────────────────────────────────────
export const Modal = ({ title, onClose, children, wide = false }) => (
  <div
    style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      overflowY: "auto", padding: "40px 16px",
    }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div style={{
      background: "#fff", borderRadius: 14, width: wide ? 820 : 580,
      maxWidth: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", position: "relative",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 24px", borderBottom: "1px solid #eee",
      }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#1a1a1a" }}>{title}</h2>
        <button
          onClick={onClose}
          style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#888", lineHeight: 1 }}
        >
          ✕
        </button>
      </div>
      <div style={{ padding: "20px 24px" }}>{children}</div>
    </div>
  </div>
);

// ── EmptyState ────────────────────────────────────────────────────────────────
export const EmptyState = ({ icon, message }) => (
  <div style={{
    textAlign: "center", padding: "48px 24px", color: "#999",
    background: "#fafafa", borderRadius: 12, border: "1px dashed #ddd",
  }}>
    <div style={{ fontSize: 40, marginBottom: 10 }}>{icon}</div>
    <p style={{ margin: 0, fontSize: 14 }}>{message}</p>
  </div>
);

// ── SectionHeader ──────────────────────────────────────────────────────────────
export const SectionHeader = ({ title, action }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1a1a1a" }}>{title}</h2>
    {action}
  </div>
);

// ── Tabs ───────────────────────────────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
    {tabs.map(t => (
      <button
        key={t.value}
        onClick={() => onChange(t.value)}
        style={{
          padding: "7px 20px", borderRadius: 8,
          border: `1px solid ${active === t.value ? "#2d7a3a" : "#ddd"}`,
          background: active === t.value ? "#e8f5e9" : "#fff",
          color: active === t.value ? "#2d7a3a" : "#666",
          fontFamily: "inherit", fontWeight: 600, fontSize: 13, cursor: "pointer",
        }}
      >
        {t.icon && <span style={{ marginRight: 6 }}>{t.icon}</span>}
        {t.label}
      </button>
    ))}
  </div>
);

// ── InfoBanner ────────────────────────────────────────────────────────────────
export const InfoBanner = ({ children, type = "info" }) => {
  const styles = {
    info:    { bg: "#eff6ff", border: "#bfdbfe", color: "#1e40af" },
    warning: { bg: "#fffbeb", border: "#fde68a", color: "#92400e" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", color: "#14532d" },
    danger:  { bg: "#fef2f2", border: "#fecaca", color: "#7f1d1d" },
  };
  const s = styles[type];
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8,
      padding: "10px 14px", marginBottom: 20, fontSize: 13, color: s.color,
    }}>
      {children}
    </div>
  );
};
